import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import { Box, IconButton } from '@material-ui/core';
import { Close, Notes } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { CircularLoader } from '@roc/ui';
import { CommunicationStore } from '../stores/communicationStore';

const Accordion = withStyles({
  root: {
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    backgroundColor: 'rgba(0, 0, 0, .03)',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 40,
    '&$expanded': {
      minHeight: 40,
    },
    '& *': {
      fontWeight: 'bold',
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    '& *': {
      fontSize: 14,
    },
  },
}))(MuiAccordionDetails);

interface ConversationSummaryProps {
  handleClose: () => void;
  communicationStore: CommunicationStore;
}

export const ConversationSummary = observer(({
  handleClose,
  communicationStore,
}: ConversationSummaryProps) => {
  const [expanded, setExpanded] = useState<number | false>(0);
  const {
    conversationSummary,
    conversationSummaryLoading,
    conversationSummaryError,
  } = communicationStore;

  const handleChange =
    (panel: number) => (_event: React.ChangeEvent<{}>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const renderContent = () => {
    if (conversationSummaryLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularLoader position="relative" thickness={5} color="primary" size={36} />
        </Box>
      );
    }

    if (conversationSummaryError) {
      return (
        <Box px={2} py={3}>
          <Typography color="error">{conversationSummaryError}</Typography>
        </Box>
      );
    }

    if (!conversationSummary?.sections?.length) {
      return (
        <Box px={2} py={3}>
          <Typography color="textSecondary">No conversation summary available</Typography>
        </Box>
      );
    }

    return conversationSummary.sections.map((section, index) => (
      <Accordion
        key={`${section.section_name}-${section.section_order}`}
        square
        expanded={expanded === index}
        onChange={handleChange(index)}
      >
        <AccordionSummary aria-controls={`summary-section-${index}-content`} id={`summary-section-${index}-header`}>
          <Typography style={{ fontSize: '14px', fontWeight: 'bold' }}>
            {`${index + 1}. ${section.section_name}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ul" m={0} pl={2}>
            {section.bullets?.map((bullet, bulletIndex) => (
              <Box component="li" key={`${section.section_name}-${bulletIndex}`} mb={0.5}>
                {bullet}
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Box className="conversation-summary">
      <Box
        style={{ borderLeft: '1px solid rgba(0, 0, 0, .125)' }}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography
            variant="h6"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px',
            }}
          >
            <Notes style={{ marginRight: 10 }} />
            AI Summary
          </Typography>
        </Box>
        <Box m="auto 20px">
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>
      {renderContent()}
    </Box>
  );
});
